import { ReactNode, useCallback, useContext } from 'react';
import { View } from '@shared/components/View';
import { MBButton } from '@shared/components/button';
import { MBIconV2 } from '@shared/components/image';
import { MBModal } from '@shared/components/modal';
import { MBTextBody1Bold, MBTextBody2 } from '@shared/components/typography';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';

export interface UpgradeContentProps {
  title: string;
  description: string;
  header?: ReactNode;
  onUpgrade?: () => void;
  onLearnMore?: () => void;
  onDismiss?: () => void;
}

export function UpgradeContent({
  title,
  description,
  header,
  onUpgrade,
  onLearnMore,
  onDismiss,
}: UpgradeContentProps) {
  const colors = useContext(ColorThemeContext);

  return (
    <View center paddingHorizontal={4} paddingBottom={4}>
      {header ?? <MBIconV2 name="lock" color={colors.accent} />}
      <View paddingTop={2} />

      <MBTextBody1Bold color={colors.foregroundActive}>{title}</MBTextBody1Bold>
      <View row paddingTop={1} />
      <MBTextBody2 color={colors.foregroundActive} style={{ textAlign: 'center' }}>
        {description}
      </MBTextBody2>
      <View row paddingTop={4}>
        {!!onLearnMore ? (
          <MBButton
            title={i18n('common.learnMore')}
            size="small"
            theme="outline"
            onPress={onLearnMore}
          />
        ) : !!onDismiss ? (
          <MBButton
            title={!!onUpgrade ? i18n('common.close') : i18n('common.ok')}
            size="small"
            theme="outline"
            onPress={onDismiss}
          />
        ) : null}
        {(!!onLearnMore || !!onDismiss) && !!onUpgrade && <View paddingLeft={2} />}
        {!!onUpgrade && (
          <MBButton title={i18n('common.upgrade')} size="small" onPress={onUpgrade} />
        )}
      </View>
    </View>
  );
}

export interface Props {
  title: string;
  description: string;
  visible: boolean;
  header?: ReactNode;
  onClose(): void;
  onUpgrade(): void;
  onLearnMore?(): void;
}

export function UpgradeModal({
  title,
  description,
  visible,
  header,
  onClose,
  onLearnMore,
  onUpgrade,
}: Props) {
  const handleUpgrade = useCallback(() => {
    onClose();
    onUpgrade();
  }, [onUpgrade, onClose]);
  const handleLearnMore = useCallback(() => {
    onClose();
    onLearnMore?.();
  }, [onLearnMore, onClose]);

  return (
    <MBModal
      webOverlayStyle={{ zIndex: 999 }}
      webContainerStyle={{ width: '100%' }}
      visible={visible}
      onClose={onClose}
      padding={0}
    >
      <UpgradeContent
        title={title}
        description={description}
        header={header}
        onUpgrade={handleUpgrade}
        onLearnMore={onLearnMore ? handleLearnMore : undefined}
        onDismiss={onClose}
      />
    </MBModal>
  );
}
